"use client";

import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@ospra/design-system/components/sidebar";
import { cn } from "@ospra/design-system/lib/utils";

import type { NavItem } from "@/config/nav";
import { useAuth } from "@/hooks/useAuth";
import type { OrgType } from "@/types/orgTypes";

function NavItemComponent({
  item,
  currentPath,
  role,
  orgType,
  itemButtonClassName,
  showLabel,
}: {
  item: NavItem;
  currentPath: string;
  role: string | null;
  orgType: OrgType | null;
  itemButtonClassName?: string;
  showLabel: boolean;
}) {
  if (
    item.roles &&
    role &&
    !item.roles.includes(role as "admin" | "member" | "owner")
  ) {
    return null;
  }

  if (item.orgTypes && orgType && !item.orgTypes.includes(orgType)) {
    return null;
  }

  const visibleSubItems = item.items?.filter(
    (subItem) =>
      !(subItem.orgTypes && orgType) || subItem.orgTypes.includes(orgType)
  );

  const hasSubItems = !!visibleSubItems?.length;
  const isActive =
    item.url === "/"
      ? currentPath === "/" || currentPath === "/overview"
      : currentPath.startsWith(item.url) ||
        (hasSubItems &&
          visibleSubItems.some(
            (subItem) =>
              subItem.url !== "#" && currentPath.startsWith(subItem.url)
          ));

  const displayUrl =
    hasSubItems && visibleSubItems?.[0] ? visibleSubItems[0].url : item.url;

  return (
    <SidebarMenuItem className="group/nav-item" key={item.name}>
      <SidebarMenuButton
        className={cn("relative", itemButtonClassName)}
        isActive={isActive}
        render={
          <Link to={displayUrl}>
            <item.icon />
            {/* Single always-present span — animates width+opacity so icon never jumps */}
            <span
              className={cn(
                "overflow-hidden whitespace-nowrap transition-[max-width,opacity]",
                showLabel
                  ? "max-w-[200px] opacity-100 delay-[180ms] duration-200"
                  : "max-w-0 opacity-0 duration-100"
              )}
            >
              {item.name}
            </span>
          </Link>
        }
      />
      {showLabel && (
        <>
          {item.badge ? (
            <SidebarMenuBadge className="top-2! opacity-50 transition-opacity group-hover/nav-item:opacity-100">
              {item.badge}
            </SidebarMenuBadge>
          ) : (
            hasSubItems && (
              <SidebarMenuBadge className="top-2! opacity-40 transition-all duration-200 group-hover/nav-item:opacity-100 group-has-data-[active=true]/nav-item:opacity-100">
                <ChevronRight
                  className={cn(
                    "size-3.5 transition-transform duration-300 ease-in-out",
                    isActive ? "rotate-90" : "rotate-0"
                  )}
                />
              </SidebarMenuBadge>
            )
          )}
          {hasSubItems && (
            <div
              className={cn(
                "grid overflow-hidden transition-all duration-300 ease-in-out",
                isActive
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0 group-hover/nav-item:grid-rows-[1fr] group-hover/nav-item:opacity-100"
              )}
            >
              <SidebarMenuSub className="min-h-0">
                {visibleSubItems?.map((subItem) => (
                  <SidebarMenuSubItem key={subItem.name}>
                    <SidebarMenuSubButton
                      isActive={currentPath.startsWith(subItem.url)}
                      render={
                        <Link to={subItem.url}>
                          {subItem.icon && (
                            <subItem.icon className="size-3.5 opacity-70" />
                          )}
                          <span>{subItem.name}</span>
                        </Link>
                      }
                    />
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </div>
          )}
        </>
      )}
    </SidebarMenuItem>
  );
}

export function NavItems({
  items,
  itemButtonClassName,
  showLabel = true,
  ...props
}: {
  items: NavItem[];
  itemButtonClassName?: string;
  showLabel?: boolean;
} & React.ComponentPropsWithoutRef<typeof SidebarGroup>) {
  const { role, orgType } = useAuth();
  const location = useLocation();
  const currentPath = location.pathname;

  return (
    <SidebarGroup {...props}>
      <SidebarMenu>
        {items.map((item) => (
          <NavItemComponent
            currentPath={currentPath}
            item={item}
            itemButtonClassName={itemButtonClassName}
            key={item.name}
            orgType={orgType}
            role={role}
            showLabel={showLabel}
          />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
